import React from 'react';

import { withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';

import { ICarsDetailsState, ICarsReviewBookState } from '@store/slices';
import { ILoginState, IMenuState } from '@share/store/slices';
import { RootState, computeIsStripePaymentMethodsEnabled, isEnableCreditLine } from '@share/utils';

import { CarsInfo } from '../cars-info';
import { CarsLocations } from '../cars-location';
import { CarsPolicies } from '../cars-policies';
import { CarsExtras } from '../cars-extras';

import { CarsPayment } from '../cars-payment';
import { CarsDisclaimers } from '../cars-disclaimers';
import { FormInstance } from 'antd/lib/form';
import { DriverInformation } from '../cars-driver-information';
import { CarsConfirmationEmail } from '../cars-confirmation-email';
import BillingAddressForm from '../billing-address-form/component';

import { CarsDetailsAnchors } from '../cars-details-anchors';

import './style.scss';
import { ButtonStripeComplete } from '../cars-details-wrapper';
import { isEmpty } from 'lodash';

interface IMapStateToProps {
  carsDetailsStore: ICarsDetailsState;
  carsReviewBookStore: ICarsReviewBookState;
  loginStore: ILoginState;
  navigationMenuStore: IMenuState;
}

interface IProps extends IMapStateToProps, RouteComponentProps {
  dealId: number;
  backHomeType: string;

  isCheckout: boolean;

  overviewRef: React.RefObject<HTMLDivElement>;
  paymentRef: React.RefObject<HTMLDivElement>;
  driverRef: React.RefObject<HTMLDivElement>;
  emailConfirmationRef: React.RefObject<HTMLDivElement>;
  reserveRef: React.RefObject<HTMLDivElement>;
  locationRef: React.RefObject<HTMLDivElement>;
  policiesRef: React.RefObject<HTMLDivElement>;
  extrasRef: React.RefObject<HTMLDivElement>;

  paymentFormRef: React.RefObject<FormInstance>;
  driverFormRef: React.RefObject<FormInstance>;
  emailFormRef: React.RefObject<FormInstance>;
  billingFormRef: React.RefObject<FormInstance>;
}

export interface IButtonsComplete {
  driverForm: boolean;
  paymentForm: boolean;
  emailForm: boolean;
  billingForm: boolean;
}

interface IMultiplePayments {
  amazonPay: boolean;
  applePay: boolean;
  googlePay: boolean;
  link: boolean;
  paypal: boolean;
}

interface IState {
  isButtonCompleteHidden: IButtonsComplete;
  multiplePayments: IMultiplePayments;
  paymentSelected: string;
  scrollToPaymentSelect: boolean;
  ifEmailChanging: string | undefined;
  expressWalletProbeComplete: boolean;
  hasExpressWalletMethods: boolean;
}

class CarsDetailsComponent extends React.Component<IProps, IState> {
  state: IState = {
    isButtonCompleteHidden: {
      driverForm: false,
      paymentForm: false,
      emailForm: false,
      billingForm: false
    },
    multiplePayments: {
      amazonPay: false,
      applePay: false,
      googlePay: false,
      link: false,
      paypal: false
    },
    paymentSelected: '',
    scrollToPaymentSelect: false,
    ifEmailChanging: undefined,
    expressWalletProbeComplete: false,
    hasExpressWalletMethods: false,
  };

  onExpressWalletAvailabilityResolved = (hasWallets: boolean) => {
    this.setState({
      expressWalletProbeComplete: true,
      hasExpressWalletMethods: hasWallets,
    });
  };

  render(): React.ReactNode {
    const { carsDetailsStore, carsReviewBookStore, loginStore, navigationMenuStore, isCheckout, paymentFormRef, driverFormRef, emailFormRef, billingFormRef } = this.props;
    const { car } = carsDetailsStore;
    const { bookingComplete } = carsReviewBookStore;

    if (bookingComplete) {
      return null;
    }

    const { account } = loginStore;
    const skipPaymentCheckout = isEnableCreditLine(account);
    const stripeRegionOk =
      !skipPaymentCheckout &&
      computeIsStripePaymentMethodsEnabled(
        account,
        navigationMenuStore?.items?.siteId,
        navigationMenuStore?.items?.organizationId,
      );
    const showExpressCheckoutFlow =
      stripeRegionOk &&
      this.state.expressWalletProbeComplete &&
      this.state.hasExpressWalletMethods;
    const paymentEffective = showExpressCheckoutFlow ? this.state.paymentSelected : 'card';

    const isCardPayment =
      !skipPaymentCheckout && !isEmpty(paymentEffective) && paymentEffective === 'card';
    const isOtherPayment =
      showExpressCheckoutFlow &&
      !isEmpty(this.state.paymentSelected) &&
      this.state.paymentSelected !== 'card';

    const shouldMountStripeExpressElements =
      stripeRegionOk &&
      (!this.state.expressWalletProbeComplete || this.state.hasExpressWalletMethods);
    const isEmailChanging = (ifEmailChanging: string | undefined) => this.setState({ifEmailChanging})

    const scrollToPaymentSelectDisabled = () => this.setState({ scrollToPaymentSelect: false });
    const scrollToPaymentSelectEnabled = () => this.setState({ scrollToPaymentSelect: true });

    const handleDriverFormValidation = (driverForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          driverForm,
        },
      }));
    };

    const handleEmailFormValidation = (emailForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          emailForm,
        },
      }));
    };

    const handlePaymentFormValidation = (paymentForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          paymentForm,
        },
      }));
    };

    const handleBillingFormValidation = (billingForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          billingForm,
        },
      }));
    };

    // Define keysToValidate with a type assertion
    const keysToValidate: Array<keyof IButtonsComplete> = skipPaymentCheckout
      ? ['driverForm', 'emailForm', 'billingForm']
      : isCardPayment
        ? ['driverForm', 'emailForm', 'paymentForm']
        : ['driverForm', 'emailForm', 'billingForm'];

    // Validate only the selected keys
    //VALIDATE THE SELECT PAYMENT FORM
    const VALIDATE_STRIPE_BTNS =
      skipPaymentCheckout || !isEmpty(paymentEffective)
        ? keysToValidate.every((key) => this.state.isButtonCompleteHidden[key] === true)
        : false;

    const setMultiplePayments = (payments: any) => {
      this.setState((prevState: IState) => ({
        ...prevState,
        multiplePayments: {
          ...payments,
        },
      }));
    };

    const setPaymentSelected = (payment: string) => {
      this.setState((prevState: IState) => ({
        ...prevState,
        paymentSelected: payment,
      }));
    };

    return (
      <>
        <CarsDetailsAnchors
          overviewRef={this.props.overviewRef}
          paymentRef={this.props.paymentRef}
          driverRef={this.props.driverRef}
          emailConfirmationRef={this.props.emailConfirmationRef}
          reserveRef={this.props.reserveRef}
          locationRef={this.props.locationRef}
          policiesRef={this.props.policiesRef}
          extrasRef={this.props.extrasRef}
          isCheckout={isCheckout}
        />

        <div ref={this.props.overviewRef} />
        <CarsInfo car={car} />

        {isCheckout ? (
          <>
            <div ref={this.props.driverRef} />
            <DriverInformation
              formRef={this.props.driverFormRef}
              car={car}
              handleDriverFormValidation={handleDriverFormValidation}
              emailRef={this.props.emailFormRef}
              isEmailChanging={isEmailChanging}
            />

            {!skipPaymentCheckout ? (
              <>
                <div ref={this.props.paymentRef} />
                <CarsPayment
                  formRef={this.props.paymentFormRef}
                  billingRef={this.props.billingFormRef}
                  car={car}
                  handleBillingFormValidation={handleBillingFormValidation}
                  multiplePayments={this.state.multiplePayments}
                  setPaymentSelected={setPaymentSelected}
                  paymentSelected={paymentEffective}
                  stripeExpressEnabled={showExpressCheckoutFlow}
                  handlePaymentFormValidation={handlePaymentFormValidation}
                  scrollToPaymentSelect={this.state.scrollToPaymentSelect}
                  scrollToPaymentSelectDisabled={scrollToPaymentSelectDisabled}
                />
              </>
            ) : (
              <div className="hotel-info">
                <div className="hotel-info__item border-top">
                  <div className="hotel-info__item-content">
                    <BillingAddressForm
                      formRef={this.props.billingFormRef}
                      loading={carsReviewBookStore.loadingBooking}
                      isRequired
                      handleBillingFormValidation={handleBillingFormValidation}
                      forceDisplay
                    />
                  </div>
                </div>
              </div>
            )}

            <div ref={this.props.emailConfirmationRef} />
            <CarsConfirmationEmail
              formRef={this.props.emailFormRef}
              handleEmailFormValidation={handleEmailFormValidation}
              ifEmailChanging={this.state.ifEmailChanging}
            />

            <div ref={this.props.reserveRef} />
            <CarsDisclaimers
              driverFormRef={this.props.driverFormRef}
              paymentFormRef={this.props.paymentFormRef}
              billingRef={this.props.billingFormRef}
              emailFormRef={this.props.emailFormRef}
              isButtonCompleteHidden={this.state.isButtonCompleteHidden}
              setMultiplePayments={setMultiplePayments}
              paymentSelected={paymentEffective}
              expressCheckoutPaymentSelected={this.state.paymentSelected}
              validateStripeBtns={VALIDATE_STRIPE_BTNS}
              scrollToPaymentSelectDisabled={scrollToPaymentSelectDisabled}
              scrollToPaymentSelectEnabled={scrollToPaymentSelectEnabled}
              shouldMountStripeExpressElements={shouldMountStripeExpressElements}
              expressWalletProbeComplete={this.state.expressWalletProbeComplete}
              onExpressWalletAvailabilityResolved={this.onExpressWalletAvailabilityResolved}
            />
          </>
        ) : (
          <>
            <div ref={this.props.locationRef} />
            <CarsLocations car={car} />

            <div ref={this.props.policiesRef} />
            <CarsPolicies car={car} />

            <CarsExtras display={!isCheckout} extrasRef={this.props.extrasRef} />
          </>
        )}
      </>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    carsDetailsStore: state.carsDetailsStore,
    carsReviewBookStore: state.carsReviewBookStore,
    loginStore: state.loginStore,
    navigationMenuStore: state.navigationMenuStore,
  };
};

export const CarsDetails = connect(mapStateToProps)(withRouter(CarsDetailsComponent));

